import type { ApiResponse, AuthResponse, User } from '@/types';

// Формируем BASE_URL с /api суффиксом
function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;

  if (!envUrl) {
    return '/api';
  }

  // Если URL уже заканчивается на /api, используем как есть
  if (envUrl.endsWith('/api')) {
    return envUrl;
  }

  // Убираем trailing slash и добавляем /api
  return envUrl.replace(/\/$/, '') + '/api';
}

const API_BASE_URL = getApiBaseUrl();

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('auth_token');
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  // Добавляем Telegram initData если доступно
  if (window.Telegram?.WebApp?.initData) {
    (headers as Record<string, string>)['X-Telegram-Init-Data'] = window.Telegram.WebApp.initData;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });
  
  const data: ApiResponse<T> = await response.json();
  
  if (!response.ok || !data.success) {
    throw new ApiError(response.status, data.error || 'Unknown error');
  }
  
  return data.data as T;
}

// ============================================
// AUTH
// ============================================

export async function authenticateTelegram(): Promise<AuthResponse> {
  return request<AuthResponse>('/auth/telegram', {
    method: 'POST',
  });
}

export async function authenticateDev(telegramId: number, firstName?: string): Promise<AuthResponse> {
  return request<AuthResponse>('/auth/dev', {
    method: 'POST',
    body: JSON.stringify({ telegramId, firstName }),
  });
}

export async function getCurrentUser(): Promise<{ user: User }> {
  return request<{ user: User }>('/auth/me');
}

// ============================================
// DIAGNOSIS
// ============================================

export async function startDiagnosis(): Promise<{ sessionId: string; message: string }> {
  return request('/diagnosis/start', {
    method: 'POST',
  });
}

export async function sendDiagnosisAnswer(sessionId: string, answer: string): Promise<{
  message: string;
  stage: string;
  isComplete: boolean;
  character?: unknown;
}> {
  return request('/diagnosis/answer', {
    method: 'POST',
    body: JSON.stringify({ sessionId, answer }),
  });
}

// ============================================
// CHARACTER
// ============================================

export async function getCharacter(): Promise<{ character: unknown }> {
  return request('/character');
}

export async function updateCharacterName(name: string): Promise<{ character: unknown }> {
  return request('/character/name', {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

// Export API instance for custom requests
export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),
  post: <T>(endpoint: string, data?: unknown) => 
    request<T>(endpoint, { method: 'POST', body: data ? JSON.stringify(data) : undefined }),
  put: <T>(endpoint: string, data?: unknown) => 
    request<T>(endpoint, { method: 'PUT', body: data ? JSON.stringify(data) : undefined }),
  delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
};

// Telegram WebApp type declaration
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            language_code?: string;
            photo_url?: string;
          };
        };
        ready: () => void;
        expand: () => void;
        close: () => void;
        MainButton: {
          text: string;
          show: () => void;
          hide: () => void;
          onClick: (callback: () => void) => void;
        };
        BackButton: {
          show: () => void;
          hide: () => void;
          onClick: (callback: () => void) => void;
        };
        HapticFeedback: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
        };
        themeParams: {
          bg_color?: string;
          text_color?: string;
          hint_color?: string;
          link_color?: string;
          button_color?: string;
          button_text_color?: string;
        };
        colorScheme: 'light' | 'dark';
        viewportHeight: number;
        viewportStableHeight: number;
        openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
        openTelegramLink: (url: string) => void;
      };
    };
  }
}
